/**
 * Onglet Compétitions — page hub + sous-navigation interne
 *
 * Affiche une page d'accueil listant les différentes compétitions consultables,
 * puis monte le composant correspondant lorsqu'une compétition est sélectionnée.
 */

import { useState, useCallback, useEffect } from 'react';
import DiffereResults from './DiffereResults';
import LiveRanking from './competitions/LiveRanking';
import CIAPage from './competitions/CIAPage';
import TeamChampionshipPage from './competitions/TeamChampionshipPage';
import TeamsListPage from './competitions/TeamsListPage';
import MatchHistoryPage from './competitions/MatchHistoryPage';
import TeamHubPage from './competitions/TeamHubPage';
import NoTeamPanel from './competitions/NoTeamPanel';
import { PlayerAvatar } from '../components/PlayerAvatar';
import {
  DiffereIcon,
  LiveBroadcastIcon,
  MedalIcon,
  GroupIcon,
  ListGridIcon,
  ChartTrendIcon,
  PersonAddIcon,
} from '../components/Icons';
import type { Session } from '../types/app';
import { useApp } from '../contexts/AppContext';
import { useTabReselect } from '../hooks/useTabReselect';
import { getMyTeam, type MyTeam } from '../lib/teamsApi';
import { BACKEND_URL } from '../config';
import '../styles/competitions.css';

type CompetitionView =
  | 'hub' | 'differe' | 'live' | 'cia' | 'team-champ'
  | 'teams-list' | 'matches-history' | 'team-hub' | 'no-team';

interface CompetitionsPageProps {
  session: Session;
  isAdmin?: boolean;
}

interface CompetitionCardDef {
  id: CompetitionView;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
}

const COMPETITIONS: CompetitionCardDef[] = [
  {
    id: 'differe',
    title: 'Résultats Différé',
    subtitle: 'Tournois différés individuel, triplette et quadrette',
    icon: <DiffereIcon size={32} />,
  },
  {
    id: 'live',
    title: 'Classement Live',
    subtitle: 'Classement général en temps réel de l\'espace compétition',
    icon: <LiveBroadcastIcon size={32} />,
  },
  {
    id: 'cia',
    title: 'Championnat Individuel Attaque',
    subtitle: 'Phases, divisions, classements, défis et rendez-vous CIA',
    icon: <MedalIcon size={32} />,
  },
  {
    id: 'team-champ',
    title: 'Championnat par Équipes',
    subtitle: 'Phases, divisions, classements et résultats par équipe',
    icon: <GroupIcon size={32} />,
  },
];

function fmtDeadline(v: string | null): string {
  if (!v) return '';
  try {
    const d = new Date(v);
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
      + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return v;
  }
}

export default function CompetitionsPage({ session, isAdmin = false }: CompetitionsPageProps) {
  const { differeNavigationRequest } = useApp();
  const [activeView, setActiveView] = useState<CompetitionView>(
    differeNavigationRequest ? 'differe' : 'hub',
  );

  useEffect(() => {
    if (differeNavigationRequest) setActiveView('differe');
  }, [differeNavigationRequest]);

  const goBack = useCallback(() => setActiveView('hub'), []);
  useTabReselect('competitions', goBack);

  // --- Statut équipe du joueur (pour les cartes hub) ---
  const [myTeam, setMyTeam] = useState<MyTeam | null>(null);
  const [myTeamLoaded, setMyTeamLoaded] = useState(false);

  const refreshMyTeam = useCallback(() => {
    if (!session.token) { setMyTeam(null); setMyTeamLoaded(true); return; }
    getMyTeam(session.token)
      .then((d) => setMyTeam(d.team))
      .catch(() => setMyTeam(null))
      .finally(() => setMyTeamLoaded(true));
  }, [session.token]);

  useEffect(() => { refreshMyTeam(); }, [refreshMyTeam]);

  // Si le joueur quitte son équipe (ou l'équipe change), retourner au hub si on était dans son espace équipe.
  useEffect(() => {
    if (activeView === 'team-hub' && myTeamLoaded && !myTeam) setActiveView('hub');
  }, [activeView, myTeamLoaded, myTeam]);

  if (activeView === 'differe') {
    return (
      <div className="competitions-wrapper">
        <CompetitionsBackBar onBack={goBack} title="Résultats Différé" />
        <div className="competitions-subview">
          <DiffereResults session={session} />
        </div>
      </div>
    );
  }

  if (activeView === 'live') {
    return (
      <div className="competitions-wrapper">
        <CompetitionsBackBar onBack={goBack} title="Classement Live" />
        <div className="competitions-subview">
          <LiveRanking session={session} />
        </div>
      </div>
    );
  }

  if (activeView === 'cia') {
    return (
      <div className="competitions-wrapper">
        <CompetitionsBackBar onBack={goBack} title="Championnat Individuel Attaque" />
        <div className="competitions-subview">
          <CIAPage session={session} />
        </div>
      </div>
    );
  }

  if (activeView === 'team-champ') {
    return (
      <div className="competitions-wrapper">
        <CompetitionsBackBar onBack={goBack} title="Championnat par Équipes" />
        <div className="competitions-subview">
          <TeamChampionshipPage session={session} isAdmin={isAdmin} />
        </div>
      </div>
    );
  }

  if (activeView === 'teams-list') {
    return (
      <div className="competitions-wrapper">
        <CompetitionsBackBar onBack={goBack} title="Équipes" />
        <div className="competitions-subview">
          <TeamsListPage session={session} />
        </div>
      </div>
    );
  }

  if (activeView === 'matches-history') {
    return (
      <div className="competitions-wrapper">
        <CompetitionsBackBar onBack={goBack} title="Historique des défis" />
        <div className="competitions-subview">
          <MatchHistoryPage session={session} />
        </div>
      </div>
    );
  }

  if (activeView === 'no-team') {
    return (
      <div className="competitions-wrapper">
        <CompetitionsBackBar onBack={goBack} title="Rejoindre / créer une équipe" />
        <div className="competitions-subview">
          <NoTeamPanel session={session} onTeamCreated={() => { refreshMyTeam(); setActiveView('team-hub'); }} />
        </div>
      </div>
    );
  }

  if (activeView === 'team-hub' && myTeam) {
    return (
      <div className="competitions-wrapper">
        <CompetitionsBackBar onBack={goBack} title="Mon équipe" />
        <div className="competitions-subview">
          <TeamHubPage
            session={session}
            team={myTeam}
            onChanged={refreshMyTeam}
            onLeft={() => { refreshMyTeam(); setActiveView('hub'); }}
          />
        </div>
      </div>
    );
  }

  // Hub view
  return (
    <div className="competitions-hub">
      <h1 className="competitions-hub-title">Compétitions</h1>
      <p className="competitions-hub-subtitle">
        Consultez les résultats et classements des différentes compétitions
      </p>
      <div className="competitions-cards">
        {myTeam && (
          <button className="competition-card" onClick={() => setActiveView('team-hub')}>
            <PlayerAvatar
              pseudo={myTeam.nom}
              size={44}
              photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(myTeam.nom)}?size=88`}
            />
            <div className="competition-card-text">
              <span className="competition-card-title">{myTeam.nom}</span>
              <span className="competition-card-subtitle">
                {myTeam.next_deadline
                  ? `Prochaine échéance : ${fmtDeadline(myTeam.next_deadline.rdv)}`
                  : 'Gestion, agenda et forum de votre équipe'}
              </span>
            </div>
          </button>
        )}

        {myTeamLoaded && !myTeam && (
          <button className="competition-card" onClick={() => setActiveView('no-team')}>
            <div className="competition-card-icon"><PersonAddIcon size={32} /></div>
            <div className="competition-card-text">
              <span className="competition-card-title">Rejoindre une équipe</span>
              <span className="competition-card-subtitle">Créer une équipe ou postuler à une équipe qui recrute</span>
            </div>
          </button>
        )}

        {COMPETITIONS.map((comp) => (
          <button
            key={comp.id}
            className="competition-card"
            onClick={() => setActiveView(comp.id)}
          >
            <div className="competition-card-icon">{comp.icon}</div>
            <div className="competition-card-text">
              <span className="competition-card-title">{comp.title}</span>
              <span className="competition-card-subtitle">{comp.subtitle}</span>
            </div>
          </button>
        ))}

        <button className="competition-card" onClick={() => setActiveView('teams-list')}>
          <div className="competition-card-icon"><ListGridIcon size={32} /></div>
          <div className="competition-card-text">
            <span className="competition-card-title">Équipes</span>
            <span className="competition-card-subtitle">Rechercher parmi les équipes existantes</span>
          </div>
        </button>

        <button className="competition-card" onClick={() => setActiveView('matches-history')}>
          <div className="competition-card-icon"><ChartTrendIcon size={32} /></div>
          <div className="competition-card-text">
            <span className="competition-card-title">Historique des défis</span>
            <span className="competition-card-subtitle">Tous les défis joués entre équipes, avec filtres</span>
          </div>
        </button>
      </div>
    </div>
  );
}

function CompetitionsBackBar({ onBack, title }: { onBack: () => void; title: string }) {
  return (
    <div className="competitions-back-bar">
      <button className="competitions-back-btn" onClick={onBack} title="Retour aux compétitions">
        <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
        </svg>
        <span>Compétitions</span>
      </button>
      <span className="competitions-back-title">{title}</span>
    </div>
  );
}
